
import { useState } from 'react';

interface TrackingFormProps {
  onSearch: (query: string, type: 'id' | 'email' | 'tracking') => void;
  loading: boolean;
}

export default function TrackingForm({ onSearch, loading }: TrackingFormProps) {
  const [searchType, setSearchType] = useState<'id' | 'email' | 'tracking'>('tracking');
  const [query, setQuery] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    try {
      onSearch(trimmed, searchType);
    } catch (err) {
      console.error('TrackingForm: onSearch threw an error', err);
    }
  };

  const handleToggle = (type: 'id' | 'email' | 'tracking') => {
    setSearchType(type);
    setQuery('');
  };

  const placeholders: Record<string, string> = {
    tracking: 'Entrez votre numéro de tracking (ex: 1Z999AA1012...)',
    id: 'Entrez votre numéro de commande (ex: a1b2c3d4-...)',
    email: 'Entrez votre adresse email',
  };

  const icons: Record<string, string> = {
    tracking: 'ri-barcode-line',
    id: 'ri-file-list-3-line',
    email: 'ri-mail-line',
  };

  return (
    <section className="max-w-2xl mx-auto -mt-16 relative z-20 px-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center">
            <i className="ri-search-line text-lg text-[#2E7D32]"></i>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Rechercher votre commande</h2>
            <p className="text-xs text-gray-500">
              Recherchez par numéro de tracking, commande ou email
            </p>
          </div>
        </div>

        {/* Toggle */}
        <div className="flex bg-gray-100 rounded-full p-1 mb-6">
          <button
            type="button"
            onClick={() => handleToggle('tracking')}
            className={`flex-1 py-2.5 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
              searchType === 'tracking'
                ? 'bg-white text-[#2E7D32] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-barcode-line mr-1"></i>
            N° de tracking
          </button>
          <button
            type="button"
            onClick={() => handleToggle('id')}
            className={`flex-1 py-2.5 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
              searchType === 'id'
                ? 'bg-white text-[#2E7D32] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-hashtag mr-1"></i>
            N° commande
          </button>
          <button
            type="button"
            onClick={() => handleToggle('email')}
            className={`flex-1 py-2.5 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
              searchType === 'email'
                ? 'bg-white text-[#2E7D32] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-mail-line mr-1"></i>
            Email
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <i
              className={`${icons[searchType]} absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg`}
            ></i>
            <input
              type={searchType === 'email' ? 'email' : 'text'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholders[searchType]}
              className="w-full pl-11 pr-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2E7D32] focus:border-[#2E7D32] transition-all bg-gray-50/50"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="w-full py-3.5 bg-[#2E7D32] text-white text-sm font-semibold rounded-xl hover:bg-[#1B5E20] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg"></i>
                Recherche en cours...
              </>
            ) : (
              <>
                <i className="ri-search-line text-lg"></i>
                Suivre ma commande
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
